import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid, ReferenceLine,
} from "recharts";

type Status = "pass" | "weak" | "insufficient_signal" | "insufficient_window";
type VariantId = "V1_net" | "V2_net_div_dxy" | "V3_net_plus_ecb" | "V4_net_plus_ecb_div_dxy";

interface Stability {
  n: number; min: number; max: number; mean: number;
  pct_gt_0_5: number; pct_same_sign: number;
}
interface VariantSummary {
  id: VariantId;
  corr_yoy: number | null;
  corr_13w: number | null;
  best_lag_weeks: number;
  best_lag_correlation: number;
  stability: Stability;
}
interface SummaryResp {
  validation_status: Status;
  best_variant: VariantId;
  best_variant_metrics: VariantSummary;
  variants: VariantSummary[];
  variant_consistency: { passing: number; total: number };
  timeability: "timeable" | "not_timeable" | "inconclusive_small_n";
  regime_onsets: {
    count: number; hit_rate_12w_pct: number; n_with_fwd: number;
    onsets: Array<{ week: string; type: string; btc: number | null; fwd4?: number; fwd8?: number; fwd12?: number }>;
  };
  fred_units: Record<string, { units: string; factor: number }>;
  btc_source: string;
  btc_pages: number;
  weekly_points: number;
  window: { start: string; end: string };
  recommendation: string;
  run_at: string;
}

const VARIANT_LABEL: Record<VariantId, string> = {
  V1_net: "V1 · net liq",
  V2_net_div_dxy: "V2 · net liq / DXY",
  V3_net_plus_ecb: "V3 · net liq + ECB",
  V4_net_plus_ecb_div_dxy: "V4 · (net+ECB)/DXY",
};

function statusColor(s?: Status) {
  if (s === "pass") return "bg-emerald-600";
  if (s === "weak") return "bg-amber-600";
  if (s === "insufficient_window") return "bg-slate-600";
  return "bg-rose-600";
}

function fmt(n: number | null | undefined, d = 3) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toFixed(d);
}

export default function MacroDiagnostic() {
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<SummaryResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [variant, setVariant] = useState<VariantId>("V1_net");
  const [overlayMode, setOverlayMode] = useState<"change" | "levels">("change");

  const seriesQ = useQuery({
    queryKey: ["liquidity-series-cache-v2"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("liquidity_series_cache" as any)
        .select("week_ending, glp_variants, btc_close_usd, net_liq_yoy_pct, btc_yoy_pct, regime")
        .order("week_ending", { ascending: true });
      if (error) throw error;
      return ((data || []) as unknown) as Array<{
        week_ending: string;
        glp_variants: Record<string, number | null> | null;
        btc_close_usd: number | null;
        net_liq_yoy_pct: number | null;
        btc_yoy_pct: number | null;
        regime: string | null;
      }>;
    },
  });

  const rollingQ = useQuery({
    queryKey: ["liquidity-rolling-v2", variant],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("liquidity_diagnostics" as any)
        .select("metadata, run_at")
        .eq("metric", "rolling_52w_correlation")
        .order("run_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      const match = ((data || []) as any[]).find((r) => r?.metadata?.variant === variant);
      return (match?.metadata?.series || []) as Array<{ week: string; corr: number }>;
    },
  });

  const runDiagnostic = async () => {
    setRunning(true); setErr(null);
    try {
      const { data, error } = await supabase.functions.invoke("diagnose-liquidity-btc-correlation", { body: {} });
      if (error) throw error;
      const resp = data as SummaryResp;
      setSummary(resp);
      if (resp.best_variant) setVariant(resp.best_variant);
      await Promise.all([seriesQ.refetch(), rollingQ.refetch()]);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const overlayData = useMemo(() => {
    const rows = seriesQ.data || [];
    if (overlayMode === "levels") {
      return rows
        .map((r) => {
          const v = r.glp_variants?.[variant.split("_")[0] as "V1" | "V2" | "V3" | "V4"];
          if (v === null || v === undefined || !r.btc_close_usd) return null;
          return { date: r.week_ending, glp: v, btc_log: Math.log10(r.btc_close_usd) };
        })
        .filter(Boolean) as Array<{ date: string; glp: number; btc_log: number }>;
    }
    // CHANGE view: 13w log change for both glp(variant) and btc
    const series = rows.map((r) => ({
      date: r.week_ending,
      glp: r.glp_variants?.[variant.split("_")[0] as "V1" | "V2" | "V3" | "V4"] ?? null,
      btc: r.btc_close_usd ?? null,
    }));
    const out: Array<{ date: string; glp_chg: number | null; btc_chg: number | null }> = [];
    for (let i = 13; i < series.length; i++) {
      const a = series[i], b = series[i - 13];
      const glp_chg = a.glp && b.glp && a.glp > 0 && b.glp > 0
        ? (Math.log(a.glp) - Math.log(b.glp)) * 100 : null;
      const btc_chg = a.btc && b.btc && a.btc > 0 && b.btc > 0
        ? (Math.log(a.btc) - Math.log(b.btc)) * 100 : null;
      if (glp_chg !== null || btc_chg !== null) {
        out.push({ date: a.date, glp_chg, btc_chg });
      }
    }
    return out;
  }, [seriesQ.data, variant, overlayMode]);

  const currentVariant = summary?.variants.find((v) => v.id === variant);

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">Macro Diagnostic v2 — Net Liquidity vs BTC</h1>
          <p className="text-muted-foreground mt-1">
            Fed − TGA − RRP (± ECB, ± DXY), correlated on CHANGES not levels. A-priori gate is fixed in advance.
          </p>
        </div>
        <Button onClick={runDiagnostic} disabled={running} size="lg">
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : "Run Diagnostic"}
        </Button>
      </div>

      {err && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-sm text-destructive">{err}</CardContent>
        </Card>
      )}

      {summary && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle>Validation Summary</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline">BTC: {summary.btc_source} ({summary.btc_pages} pg)</Badge>
                <Badge variant="outline">
                  Consistency {summary.variant_consistency.passing}/{summary.variant_consistency.total}
                </Badge>
                <Badge variant="outline">Timeability: {summary.timeability}</Badge>
                <Badge className={statusColor(summary.validation_status)}>
                  {summary.validation_status.toUpperCase()}
                </Badge>
              </div>
            </div>
            <CardDescription>
              Best variant: <strong>{VARIANT_LABEL[summary.best_variant]}</strong> ·
              Window {summary.window?.start} → {summary.window?.end} ·
              {" "}{summary.weekly_points} weekly points · Run {new Date(summary.run_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={variant} onValueChange={(v) => setVariant(v as VariantId)}>
              <TabsList className="grid grid-cols-4 w-full">
                {(Object.keys(VARIANT_LABEL) as VariantId[]).map((id) => (
                  <TabsTrigger key={id} value={id}>{VARIANT_LABEL[id]}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {currentVariant && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="corr_yoy (52w Δ)" value={fmt(currentVariant.corr_yoy)} />
                  <Stat label="corr_13w (13w Δ)" value={fmt(currentVariant.corr_13w)} />
                  <Stat label="Best lag (weeks)" value={String(currentVariant.best_lag_weeks)}
                        hint={`r=${fmt(currentVariant.best_lag_correlation)}`} />
                  <Stat label="Rolling-r stability"
                        value={`${currentVariant.stability.pct_same_sign}%`}
                        hint={`same-sign · ${currentVariant.stability.pct_gt_0_5}% > 0.5`} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Rolling min r" value={fmt(currentVariant.stability.min)} />
                  <Stat label="Rolling max r" value={fmt(currentVariant.stability.max)} />
                  <Stat label="Rolling mean r" value={fmt(currentVariant.stability.mean)} />
                  <Stat label="Rolling n (weeks)" value={String(currentVariant.stability.n)} />
                </div>
              </>
            )}

            <div className="p-4 rounded-md bg-muted text-sm">
              <strong>Recommendation:</strong> {summary.recommendation}
            </div>

            {summary.regime_onsets.onsets.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  Regime onsets — 12w forward hit rate: {summary.regime_onsets.hit_rate_12w_pct}%
                  (n={summary.regime_onsets.n_with_fwd})
                </h4>
                <div className="text-xs max-h-56 overflow-y-auto border rounded-md divide-y">
                  {summary.regime_onsets.onsets.map((o, i) => (
                    <div key={i} className="px-3 py-1.5 flex justify-between gap-2">
                      <span>{o.week} — <strong>{o.type}</strong> · BTC ${o.btc?.toLocaleString() ?? "n/a"}</span>
                      <span className="font-mono text-muted-foreground">
                        +4w {fmt(o.fwd4, 1)}% · +8w {fmt(o.fwd8, 1)}% · +12w {fmt(o.fwd12, 1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">FRED units &amp; conversions</summary>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono">
                {Object.entries(summary.fred_units).map(([k, v]) => (
                  <div key={k}><strong>{k}</strong>: {v.units} (×{v.factor})</div>
                ))}
              </div>
            </details>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle>GLP overlay — {VARIANT_LABEL[variant]}</CardTitle>
              <CardDescription>
                {overlayMode === "change"
                  ? "13-week log change (%) for GLP and BTC — this is what we're correlating."
                  : "Levels: GLP value vs log10(BTC)."}
              </CardDescription>
            </div>
            <Tabs value={overlayMode} onValueChange={(v) => setOverlayMode(v as "change" | "levels")}>
              <TabsList>
                <TabsTrigger value="change">CHANGE</TabsTrigger>
                <TabsTrigger value="levels">LEVELS</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="h-[380px]">
          {overlayData.length === 0 ? (
            <div className="text-sm text-muted-foreground">No series data yet. Run the diagnostic.</div>
          ) : overlayMode === "change" ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={overlayData as any}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <ReferenceLine y={0} stroke="hsl(var(--border))" />
                <Line dataKey="glp_chg" name="GLP 13w Δ %" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} />
                <Line dataKey="btc_chg" name="BTC 13w Δ %" stroke="hsl(var(--destructive))" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={overlayData as any}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" dataKey="glp" name="GLP level" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} />
                <Line yAxisId="right" dataKey="btc_log" name="log10(BTC)" stroke="hsl(var(--destructive))" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rolling 52-week correlation — {VARIANT_LABEL[variant]}</CardTitle>
          <CardDescription>Pearson on the 13-week log-change series (trailing 52-week window).</CardDescription>
        </CardHeader>
        <CardContent className="h-[320px]">
          {(rollingQ.data || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">No rolling data yet for this variant. Run the diagnostic.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rollingQ.data || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} minTickGap={40} />
                <YAxis tick={{ fontSize: 10 }} domain={[-1, 1]} />
                <Tooltip />
                <ReferenceLine y={0} stroke="hsl(var(--border))" />
                <ReferenceLine y={0.5} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                <ReferenceLine y={-0.5} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                <Line dataKey="corr" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="p-3 rounded-md border bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-bold font-mono">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
